import React, { FC, useEffect, useRef, useState } from 'react';
import { Container } from '@design-system/container';
import { ExtendableImage } from '@design-system/extendable-image';
import { Typography } from '@design-system/typography';
import useJoiValidation from '@root/hooks/useJoiValidation';
import useScreenRecognition from '@root/hooks/useScreenRecognition';
import { gatsbyImageMapper } from '@theme-husky/mappers/image';
import classnames from 'classnames';

import { DEFAULT_TOOLTIP_DATA } from './constants';
import { imageMapSchema } from './ImageMap.schema';
import ImageMapArea from './ImageMapArea';
import { IImageMapProps } from './models';
import useMapResize from './useMapResize';

import './ImageMap.scss';

const ImageMap: FC<IImageMapProps> = ({
  subTitle,
  text,
  sourceImage,
  sourceImageAlt,
  sourceImageWidth,
  sourceImageHeight,
  imageMapClass,
  imageBg,
  imageBgAlt,
  map,
}) => {
  const {
    data,
  }: {
    data: IImageMapProps;
  } = useJoiValidation(imageMapSchema, 'ImageMap', {
    subTitle,
    text,
    sourceImage,
    sourceImageAlt,
    sourceImageWidth,
    sourceImageHeight,
    imageMapClass,
    imageBg,
    imageBgAlt,
  });
  const { isDesktop } = useScreenRecognition();
  const [imageWidth, setImageWidth] = useState<number>(0);
  const imageRef = useRef(null);
  const { areas } = useMapResize(
    map,
    Number(sourceImageWidth),
    Number(sourceImageHeight),
    imageWidth
  );

  const [tooltip, setTooltip] = useState(DEFAULT_TOOLTIP_DATA);

  useEffect(() => {
    setTimeout(() => {
      setImageWidth(imageRef?.current?.clientWidth);
    }, 1000);
  }, []);

  useEffect(() => {
    if (!imageRef?.current?.clientWidth) {
      return;
    }

    setTooltip(() => DEFAULT_TOOLTIP_DATA);
    setImageWidth(imageRef?.current?.clientWidth);
  }, [imageRef?.current?.clientWidth]);

  const handleMouseEnter = (content, center, activeSrc) => {
    setTooltip((prevState) => ({
      ...prevState,
      hidden: false,
      content,
      center,
      hover: activeSrc || null,
    }));
  };

  const handleMouseLeave = () => {
    setTooltip((prevState) => ({
      ...prevState,
      hidden: true,
      hover: null,
    }));
  };

  const handleClick = (e) => {
    e.preventDefault();
  };

  const handleTooltipMouseEnter = () => {
    setTooltip((prevState) => ({
      ...prevState,
      hidden: false,
    }));
  };

  const handleTooltipMouseLeave = () => {
    setTooltip((prevState) => ({
      ...prevState,
      hidden: true,
    }));
  };

  return (
    <div
      className={classnames('cds-section cds-page-block image-map', {
        [`image-map--${imageMapClass}`]: imageMapClass,
      })}
    >
      {isDesktop && data?.imageBg ? (
        <div className="image-map__imageBg">
          <ExtendableImage
            element={gatsbyImageMapper({
              alt: data.imageBgAlt || '',
              image: data.imageBg,
              objectFit: 'cover',
            })}
          />
        </div>
      ) : null}
      <Container maxWidth="max-grid" className="image-map__container">
        <div className="image-map__text">
          <Typography
            variant="body"
            size="xxl"
            content={data.text}
            align="start"
            wrapping="default"
          />
        </div>
        <div className="image-map__image-wrapper">
          <div className="image-map__image">
            <Typography
              className="image-map__subtitle"
              variant="body"
              size="xxl"
              content={data.subTitle}
              align="start"
              wrapping="default"
            />
            <img
              loading="lazy"
              src={tooltip.hover || data.sourceImage.url}
              alt={data.sourceImageAlt}
              useMap={`#${imageMapClass}`}
              ref={imageRef}
              width={sourceImageWidth}
              className="image-map__image-img"
            />
            {map?.length ? (
              <map name={imageMapClass}>
                {areas.map((area, idx) => {
                  const key = area.alt + idx;

                  return (
                    <ImageMapArea
                      handleMouseLeave={handleMouseLeave}
                      handleMouseEnter={handleMouseEnter}
                      handleClick={handleClick}
                      key={key}
                      coords={area.coords}
                      alt={area.alt}
                      shape={area.shape}
                      activeState={area.activeState}
                      center={area.center}
                      tooltip={area.tooltip}
                    />
                  );
                })}
              </map>
            ) : null}
          </div>
          {tooltip?.content ? (
            <div className="image-map__tooltip" aria-live="polite">
              <Typography
                tag="p"
                variant="body"
                size="xxl"
                hidden={tooltip.hidden}
                content={tooltip.content}
                align="start"
                wrapping="default"
                onMouseLeave={handleTooltipMouseLeave}
                onMouseEnter={handleTooltipMouseEnter}
              />
            </div>
          ) : null}
        </div>
      </Container>
    </div>
  );
};

export default ImageMap;
